import _ from 'lodash';
import router from '../../../../router';
import * as types from '../../../types';
import * as orderTypes from '../../../types';
import * as notify from '../../../../lang/en/notify';
import {percentOf} from '../../../../utils';
import {LOGON_STATUS_PT,} from '../../../../lang/en/constants';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';
// Modules
import payment from './payment';
import products from './products';
import shipping from './shipping';
import discount from './discount';
import {RepositoryError} from '../../../../repositories';

const KitsilanoOrderRepository = RepositoryFactory.get('acadia/kitsilano/orders/order');
const ShopifyOrderRepository = RepositoryFactory.get('acadia/shopify/orders/order');
const ShopifyOrderRefundRepository = RepositoryFactory.get('acadia/shopify/orders/refund');
const D365OrderRepository = RepositoryFactory.get('acadia/d365/order');
const OCAEditHistoryRepository = RepositoryFactory.get('acadia/oca/edit-history');

/**
 * Default order prototype object.
 */
const orderProto = {
  'id': null,
  'email': null,
  'total_price': null,
  'subtotal_price': null,
  'total_tax': null,
  'currency': null,
  'customer': null,
  'customer_info': null,
  'total_discounts': null,
  'total_line_items_price': null,
  'order_number': null,
  'items': [],
  'discount_applications': [],
  'discount_codes': [],
  'd365_id': null,
  'name': '',
  'note_attributes': [],
  'payment_gateway_names': [],
  'source_name': 'web',
  'fulfillment_status': null,
  'tax_lines': [],
  'tags': '',
  'contact_email': null,
  'presentment_currency': null,
  'total_line_items_price_set': {},
  'total_discounts_set': {},
  'total_shipping_price_set': {},
  'subtotal_price_set': {},
  'total_price_set': {},
  'total_tax_set': {},
  'line_items': [],
  'shipping_lines': [],
  'billing_address': null,
  'shipping_address': null,
  'refunds': [],
  'financial_status': null,
  'adjustments': []
};

/**
 * Returns the default state object.
 * @returns {Object} Default state object.
 */
const getDefaultState = () => ({
  state: null,
  loading: false,
  id: null,
  region: null,
  note: '',
  maxEligibleAmount: 0,
  order: {...orderProto},
  draftOrder: null,
  d365Order: {},
  d365Error: '',
  history: null,
  hideButton: false,
});

export default {
  state: getDefaultState(), mutations: {
    /**
     * Resets the state to default.
     * @param {Object} state - The current state.
     */
    [types.ORDER_SET_DEFAULT](state) {
      Object.assign(state, getDefaultState())
    }, /**
     * Sets the state transition.
     * @param {Object} state - The current state.
     * @param {string} transitionTo - The state to transition to.
     */
    [types.ORDER_SET_STATE](state, transitionTo) {
      state.state = transitionTo;
    }, /**
     * Sets the draft order.
     * @param {Object} state - The current state.
     * @param {Object|null} order - The draft order.
     */
    [types.ORDER_SET_DRAFT_ORDER](state, order = null) {
      if (order) {
        // Sort by SKU
        order.line_items.sort(function (a, b) {
          return ('' + a.sku).localeCompare(b.sku);
        });
      }
      state.draftOrder = order;
    }, /**
     * Sets the order ID.
     * @param {Object} state - The current state.
     * @param {string|null} id - The order ID.
     */
    [types.ORDER_SET_ID](state, id = null) {
      state.id = id;
    }, /**
     * Sets the loading status.
     * @param {Object} state - The current state.
     * @param {boolean} status - The loading status.
     */
    [types.ORDER_SET_LOADING](state, status = false) {
      state.loading = status;
    }, /**
     * Sets the D365 order.
     * @param {Object} state - The current state.
     * @param {Object|null} d365Order - The D365 order.
     */
    [types.ORDER_SET_D365](state, d365Order = null) {
      state.d365Order = d365Order;
    }, /**
     * Sets the D365 error message.
     * @param {Object} state - The current state.
     * @param {string} d365Error - The D365 error message.
     */
    [types.ORDER_SET_D365_ERROR](state, d365Error = '') {
      state.d365Error = d365Error;
    }, /**
     * Sets the maximum eligible amount.
     * @param {Object} state - The current state.
     * @param {number} maxEligibleAmount - The maximum eligible amount.
     */
    [types.ORDER_SET_MAX_ELIGIBLE_AMOUNT](state, maxEligibleAmount = 0) {
      state.maxEligibleAmount = maxEligibleAmount;
    }, /**
     * Sets the order note.
     * @param {Object} state - The current state.
     * @param {string} note - The order note.
     */
    [types.ORDER_SET_NOTE](state, note = '') {
      state.note = note;
    }, /**
     * Sets the order.
     * @param {Object} state - The current state.
     * @param {Object} order - The order.
     */
    [types.ORDER_SET_ORDER](state, order) {
      if (order) {
        // Sort by SKU
        order.line_items.sort(function (a, b) {
          return ('' + a.sku).localeCompare(b.sku);
        });

        for (let item in order) {
          if (state.order.hasOwnProperty(item)) {
            state.order[item] = order[item];
          }
        }
      } else {
        state.order = {...orderProto};
      }
    }, /**
     * Sets the region.
     * @param {Object} state - The current state.
     * @param {string|null} region - The region.
     */
    [types.ORDER_SET_REGION](state, region = null) {
      state.region = region;
    }, /**
     * Sets the edit history.
     * @param {Object} state - The current state.
     * @param {Object} history - The edit history.
     */
    [types.ORDER_SET_EDIT_HISTORY](state, history) {
      state.history = history;
    }, /**
     * Sets the hide button status.
     * @param {Object} state - The current state.
     * @param {boolean} status - The hide button status.
     */
    [types.ORDER_SET_HIDE_BUTTON](state, status = false) {
      state.hideButton = status;
    },
  }, actions: {
    /**
     * Resets the order state.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_RESET]({commit}) {
      commit(types.ORDER_SET_DEFAULT);
    }, /**
     * Cancels the draft order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_DRAFT_CANCEL]({commit, dispatch}) {
      commit(types.ORDER_SET_DRAFT_ORDER);
      router.push({path: 'update'});
      dispatch(types.SUCCESS, notify.DRAFT_ORDER_CANCELLED);
    }, /**
     * Saves the draft order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_DRAFT_SAVE]({state, commit, dispatch}) {
      const totalPrice = Number(state.payment.totalPrice);
      const maxEligibleAmount = Number(state.maxEligibleAmount);

      commit(types.ORDER_SET_LOADING, true);

      // If the total is greater than the eligible amount, we let user know
      // and do not create the draft order.
      if (totalPrice > maxEligibleAmount) {
        throw new Error(notify.ORDER_TOTAL_TOO_LARGE);
      } else {
        dispatch(types.SUCCESS, notify.DRAFT_ORDER_CREATED);
        router.push({path: 'confirm'});
        commit(types.ORDER_SET_LOADING, false);
      }
    }, /**
     * Cancels the order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_CANCEL]({state, commit, dispatch}) {
      const region = state.region;
      const orderId = state.id;
      commit(types.ORDER_SET_LOADING, true);
      ShopifyOrderRepository.cancelOrder(region, orderId).catch((error) => {
        dispatch(types.ERROR, error.message || 'An error occurred.');
      }).then(response => {
        if (response.data.status === 'success') {
          dispatch(types.SUCCESS, notify.ORDER_CANCELLED);
          commit(types.ORDER_SET_DEFAULT);
        } else {
          dispatch(types.ERROR, response.data.message || 'An error occurred.');
        }

      }).finally(() => {
        commit(types.ORDER_SET_LOADING, false, {root: true});
      });

    }, /**
     * Initializes the order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_INIT]({commit, dispatch, state}) {
      const region = state.region;
      const orderId = state.id;
      if (!router.history.current.path.includes('update')) {
        router.push({path: 'update'});
      }
      dispatch('openLock', {key: [region, orderId]}).then(() => {
        return dispatch(types.ORDER_FETCH);
      }).catch(err => {
        dispatch('releaseLock', {key: [region, orderId]});

        throw err;
      }).finally(() => {
        commit(types.ORDER_SET_STATE, 'init');
      });
    }, /**
     * Fetches the order.
     * @param {Object} context - The Vuex context.
     */
    async [types.ORDER_FETCH]({commit, dispatch, state}) {
      commit(types.ORDER_SET_LOADING, true);
      commit(types.ORDER_SET_ORDER);
      commit(types.ORDER_SET_D365_ERROR);

      const {region, id: orderId} = state;

      try {
        const shopifyResponse = await ShopifyOrderRepository.getOrderOCA(region, orderId,);
        const {order: shopifyOrder} = shopifyResponse.data;
        if (shopifyOrder['status'] === 'cancelled') {
          throw new Error('PO already cancelled');
        }
        const d365Id = shopifyOrder['d365_id'];
        if (d365Id) {
          commit(types.ORDER_SET_D365_ERROR, notify.D365_CANNOT_MODIFY_ORDER,);
        }

        // Make sure order is in a state that can be edited
        // V1 do not fetch D365 order
        /*
        await dispatch(types.ORDER_D365_FETCH, {
          region,
          d365Id,
        });
        */

        // Initialize Order
        commit(types.ORDER_SET_ORDER, shopifyOrder);
        commit(types.ORDER_SET_NOTE, shopifyOrder.note);
        commit(types.ORDER_SET_MAX_ELIGIBLE_AMOUNT, shopifyOrder.total_price);
        commit(types.ORDER_SET_PAYMENT_REFUND);

        // Initialize Modules
        dispatch(types.ORDER_PAYMENT_INIT, shopifyOrder);
        dispatch(types.ORDER_DISCOUNT_INIT, shopifyOrder);
        dispatch(types.ORDER_SHIPPING_INIT, shopifyOrder);
        dispatch(types.ORDER_PRODUCTS_INIT, shopifyOrder);
      } catch (error) {
        dispatch('releaseLock', {key: [region, orderId]});
        // propagate if error wasn't already handled
        if (!(error instanceof RepositoryError)) {
          dispatch(orderTypes.ERROR, error.message);
        }
      }

      commit(types.ORDER_SET_STATE, 'loaded');
      commit(types.ORDER_SET_LOADING, false);
    }, /**
     * Fetches the D365 order.
     * @param {Object} context - The Vuex context.
     * @param {Object} payload - The payload containing the region and d365Id.
     */
    async [types.ORDER_D365_FETCH]({commit, dispatch}, {region, d365Id}) {
      const response = await D365OrderRepository.getOrder(region, d365Id);
      const {header, lines} = response.data;
      commit(types.ORDER_SET_D365, {header, lines});

      lines.forEach((l) => {
        if (l.SalesOrderLineStatus !== 'Backorder' && l.SalesOrderLineStatus !== 'None') {
          commit(types.ORDER_SET_D365_ERROR, notify.D365_CANNOT_MODIFY_ORDER,);
        }
      });

      return {header, lines};
    }, /**
     * Refunds the payment.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_REFUND_PAYMENT]({state, commit, dispatch}) {
      commit(types.ORDER_SET_LOADING, true);
      const {id: orderId, region, payment, history} = state;
      const {refund} = payment;
      try {
        const request = ShopifyOrderRefundRepository.createRefund(region, orderId, refund);

        return request.then(response => {
          commit(types.ORDER_SET_LOADING, false);
          OCAEditHistoryRepository.update(history.id, {
            refund_processed: response.data, refunded_at: response.data.refund.processed_at,
          },);
          return response;
        }, error => {
          dispatch(orderTypes.ERROR, error);
        });
      } catch (error) {
        // propagate if error wasn't already handled
        if (!(error instanceof RepositoryError)) {
          dispatch(orderTypes.ERROR, error.message);
        }
        commit(types.ORDER_SET_LOADING, false);
      }
    }, /**
     * Updates the order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_UPDATE]({state, dispatch, commit, getters}) {
      commit(types.ORDER_SET_LOADING, true);
      const region = state.region;
      const orderId = state.id;
      const order = state.order;
      const draftOrder = getters[types.ORDER_GET_DRAFT_KITSILANO];
      const d365Order = state.d365Order;
      const note = state.note;
      const discountCode = state.discount.code;
      const refund = state.payment.refund;
      // We want to associate the order with the original ID, so we overwrite it here
      KitsilanoOrderRepository.updateOrder(region, orderId, {
        'order': order,
        'draft_order': draftOrder,
        'd365_order': d365Order,
        'discount_code': discountCode,
        'refund': refund
      }).catch((error) => {
        dispatch(types.ERROR, error.message || 'An error occurred.');
      }).then(response => {
        const {kitsilano_order: {d365_order: d365Order}} = response.data;
        const manualProcess = !!d365Order;

        if (!manualProcess) {
          dispatch(types.SUCCESS, notify.ORDER_UPDATED);
        } else {
          commit(types.ORDER_SET_D365, d365Order);
          dispatch(types.WARNING, notify.D365_ORDER_NOT_UPDATED);
        }

        // If issue with Shopify arises, then let user know.
        // if (response.data.discount.error) {
        //   dispatch(types.ERROR, response.data.discount.error);
        // }

        return OCAEditHistoryRepository.store({
          order: {
            ...draftOrder, id: orderId,
          }, refund_requested: state.payment.refund, erp_payload: d365Order, manual_process: manualProcess
        });
      }).then(response => {
        commit(types.ORDER_SET_EDIT_HISTORY, response.data);

        return ShopifyOrderRepository.updateOrder(region, orderId, {
          order: {
            note,
          },
        });
      }).then(() => {
        dispatch(types.SUCCESS, notify.ORDER_UPDATED_NOTE);
        router.push({path: 'success'});

        return dispatch('releaseLock', {key: [region, orderId]});
      }).finally(() => {
        commit(types.ORDER_SET_STATE, null);
        commit(types.ORDER_SET_LOADING, false);
      });
    },
  }, getters: {
    /**
     * Gets the customer information.
     * @param {Object} state - The current state.
     * @returns {Object} Customer information.
     */
    [types.ORDER_GET_CUSTOMER](state) {
      if (state.order.customer) {
        return {
          'id': state.order.customer.id,
        };
      }
      return {};
    }, /**
     * Gets the draft order.
     * @param {Object} state - The current state.
     * @param {Object} getters - The Vuex getters.
     * @returns {Object} Draft order.
     */
    [types.ORDER_GET_DRAFT](state, getters) {
      return {
        'name': state.order.name,
        'note': state.note,
        'customer': getters[types.ORDER_GET_CUSTOMER],
        'shipping_address': state.shipping.address,
        'billing_address': state.order.billing_address,
        'line_items': getters[types.ORDER_GET_PRODUCTS_LINE_ITEMS],
        'shipping_line': getters[types.ORDER_GET_SHIPPING_LINE],
        'tags': getters[types.ORDER_GET_TAGS],
        'applied_discount': getters[types.ORDER_GET_DISCOUNT_APPLIED],
        'order_id': state.order.id
      };
    }, /**
     * Gets the draft order for Kitsilano.
     * This should ideally be handled on the back-end, but is done on the front-end to reduce overhead for now.
     * @param {Object} state - The current state.
     * @param {Object} getters - The Vuex getters.
     * @returns {Object|null} The draft order with applied discounts, or null if no draft order exists.
     */
    [types.ORDER_GET_DRAFT_KITSILANO](state, getters) {
      //todo this should be handled on back-end, just doing it on front-end to reduce overhead for now
      if (!state.draftOrder) {
        return null;
      }

      const draftOrder = _.cloneDeep(state.draftOrder);
      const discountPercent = getters[types.ORDER_GET_DISCOUNT_PERCENT];

      if (discountPercent !== 0) {
        const {line_items: lineItems} = draftOrder;

        lineItems.forEach(lineItem => {
          let appliedDiscount = 0;

          if (lineItem['applied_discount']) {
            appliedDiscount = Number(lineItem['applied_discount']['amount']);
          }

          const price = lineItem['price'] - appliedDiscount;
          const discount = percentOf(price, discountPercent, 'ceil');

          lineItem['price'] -= Math.abs(discount);
        });

        draftOrder['line_items'] = lineItems;
      }

      return draftOrder;
    }, /**
     * Gets the tags for the order.
     * @param {Object} state - The current state.
     * @param {Object} getters - The Vuex getters.
     * @returns {string} The tags for the order, including any applied discount codes.
     */
    [types.ORDER_GET_TAGS](state, getters) {
      if (state.order.name === '') {
        return '';
      }
      const isDiscountCodeApplied = getters[types.ORDER_GET_DISCOUNT_CODE_IS_APPLIED];
      let tags = [];

      // Check for existing tags
      if (state.order.tags !== '') {
        tags = state.order.tags.split(', ');
      }

      // If we have a discount code applied, add it to the tags
      if (isDiscountCodeApplied) {
        const discountCode = 'discount:' + state.discount.code;

        tags.push(discountCode);
      }

      tags = tags.join(', ');

      return tags;
    }, /**
     * Checks if the order has a disabled status for AP.
     * @param {Object} state - The current state.
     * @returns {boolean} True if the order has a disabled status for AP, false otherwise.
     */
    [types.ORDER_GET_DISABLED_STATUS_AP](state) {
      if (state.d365Order) {
        const {state: d365OrderState} = state.d365Order;
        return LOGON_STATUS_PT.indexOf(d365OrderState) !== -1;
      }
      return false;
    }, /**
     * Checks if the order is loaded.
     * @param {Object} state - The current state.
     * @returns {boolean} True if the order is loaded, false otherwise.
     */
    [types.ORDER_IS_LOADED](state) {
      return !state.loading && (state.order.id !== null);
    },
  }, modules: {
    products, shipping, discount, payment,
  },
};
